// src/widgets/WidgetManager.tsx
import { PanelConfig, WidgetConfig, WIDGET_REGISTRY, WidgetGameState } from "./widgetConfig";
import { WidgetContainer } from "./WidgetContainer";

export class WidgetManager {
    private panelConfig: PanelConfig;
    private rootElement: HTMLElement;
    private widgetContainer: WidgetContainer;

    private timedWidgetIndex = 0;
    private timedIntervalId?: number;
    private activeTriggerTimeoutId?: number;
    private isPanelVisible = true; // Track panel visibility state
    private currentWidgetId: string | null = null; // Track current widget
    private readonly liveUpdateWidgets = ['scoreByRound']; // Widgets that need real-time updates

    constructor(panelConfig: PanelConfig, rootElement: HTMLElement) {
        this.panelConfig = panelConfig;
        this.rootElement = rootElement;

        this.widgetContainer = new WidgetContainer();
        this.rootElement.appendChild(this.widgetContainer.element);

        // Check initial visibility
        this.updatePanelVisibility({ testMode: false } as WidgetGameState);

        this.showDefaultWidget();
        this.startTimerRotation();
    }

    private async showWidget(widgetId: string, gameState?: WidgetGameState) {
        const widgetConfig = WIDGET_REGISTRY[widgetId];
        if (widgetConfig) {
            this.currentWidgetId = widgetId;
            await this.widgetContainer.hide();
            await this.widgetContainer.setWidget({ id: widgetId, ...widgetConfig }, gameState);
            this.widgetContainer.show();
        }
    }

    private async refreshCurrentWidget(gameState: WidgetGameState) {
        // Refresh current widget without hiding/showing (for live updates)
        if (this.currentWidgetId) {
            const widgetConfig = WIDGET_REGISTRY[this.currentWidgetId];
            if (widgetConfig) {
                await this.widgetContainer.setWidget({ id: this.currentWidgetId, ...widgetConfig }, gameState);
            }
        }
    }

    private showDefaultWidget() {
        if (this.isPanelVisible) {
            this.showWidget(this.panelConfig.defaultWidget);
        }
    }

    private showNextTimedWidget() {
        if (!this.isPanelVisible) return;
        
        if (this.panelConfig.timedWidgets.length > 0) {
            const widgetId = this.panelConfig.timedWidgets[this.timedWidgetIndex];
            this.showWidget(widgetId);
            this.timedWidgetIndex = (this.timedWidgetIndex + 1) % this.panelConfig.timedWidgets.length;
        }
    }

    private startTimerRotation() {
        if (this.timedIntervalId) {
            clearInterval(this.timedIntervalId);
        }
        this.timedIntervalId = window.setInterval(() => {
            if (this.isPanelVisible) {
                this.showNextTimedWidget();
            }
        }, this.panelConfig.timedDuration);
    }

    private updatePanelVisibility(gameState: WidgetGameState) {
        const shouldBeVisible = !this.panelConfig.panelPredicate || 
                               this.panelConfig.panelPredicate(gameState);
        
        if (shouldBeVisible !== this.isPanelVisible) {
            this.isPanelVisible = shouldBeVisible;
            
            if (shouldBeVisible) {
                // Panel becoming visible - show default widget
                this.rootElement.style.display = '';
                this.showDefaultWidget();
            } else {
                // Panel becoming hidden - hide container
                this.rootElement.style.display = 'none';
                this.widgetContainer.hide();
            }
        }
    }

    public updateState(gameState: WidgetGameState) {
        // Update panel visibility first
        this.updatePanelVisibility(gameState);

        if (!this.isPanelVisible) return;

        // Refresh current widget if it needs live updates
        if (this.currentWidgetId && this.liveUpdateWidgets.includes(this.currentWidgetId)) {
            this.refreshCurrentWidget(gameState);
        }

        if (!this.panelConfig.triggeredWidgets) return;

        const sortedTriggers = [...this.panelConfig.triggeredWidgets].sort((a, b) => b.priority - a.priority);

        for (const trigger of sortedTriggers) {
            if (trigger.predicate(gameState)) {
                this.showTriggeredWidget(trigger.widgetId, trigger.duration, gameState);
                return;
            }
        }
    }

    public updateSpeed(speed: number) {
        if (!this.isPanelVisible) return;
        
        const currentWidget = this.widgetContainer.currentWidgetContent;
        if (currentWidget && (currentWidget as any).updateSpeed) {
            (currentWidget as any).updateSpeed(speed);
        }
    }

    private showTriggeredWidget(widgetId: string, duration: number, gameState: WidgetGameState) {
        if (this.activeTriggerTimeoutId) {
            clearTimeout(this.activeTriggerTimeoutId);
        }

        this.showWidget(widgetId, gameState);

        this.activeTriggerTimeoutId = window.setTimeout(() => {
            this.showDefaultWidget();
            this.activeTriggerTimeoutId = undefined;
        }, duration);
    }

    public cleanup() {
        if (this.timedIntervalId) {
            clearInterval(this.timedIntervalId);
        }
        if (this.activeTriggerTimeoutId) {
            clearTimeout(this.activeTriggerTimeoutId);
        }
    }
}